import { AlertRecord, AlertInfo, AlertType, AlertCategories } from "../Types";
import * as Config from '../../config';
import { default as DefaultLocale } from '../../utils/Locale';
import * as CommonText from "../../utils/common-text";
import { noValueSign } from '../../types';
import { State } from "../../status/Types";
import { ContentCategories } from "../../types";


export class AlertLogic {
    static type: AlertType | AlertType[] = [];
    static subTypes: string[] = [];
    static category = AlertCategories.OTHER;
    static contentCategories: ContentCategories[] = [];
    static title: String | String[] | Function = DefaultLocale.gettext('Unknown Alert');

    static getInfo(alert: AlertRecord, allConfigs: Config.AllConfig, states: State[]): AlertInfo {
        // `this` refers to the constructor of the class this method is called on.
        // This ensures that subclasses of AlertLogic instantiate themselves, not the base class.
        return (new this(alert, allConfigs, states)).getInfo();
    }

    static mock(type:AlertType, config:Config.AllConfig, subType?:string):AlertRecord|AlertRecord[] {
        return null;
    }

    alert: AlertRecord;
    allConfigs:Config.AllConfig;
    states: State[];
    locale:any;

    constructor(alert: AlertRecord, allConfigs: Config.AllConfig, states: State[] = [], locale?) {
        this.alert = alert;
        this.allConfigs = allConfigs;
        this.states = states;
        this.locale = locale || DefaultLocale;
    }

    getCommons(alert: AlertRecord = this.alert, config: Config.AllConfig = this.allConfigs): {
        body: Config.BodyOfWaterConfig,
        device: Config.DeviceConfig,
        alertData: any,
        /** Formatted date string or text: "Unknown" */
        firstReported: string,
        /** Formatted date string or text: "Never communicate" */
        lastSeen: string
    } {
        let body = { Name: 'BodyUuid: ' + (alert?.bodyUuid ?? noValueSign) } as Config.BodyOfWaterConfig;
        if (alert?.bodyUuid) {
            body = config?.Config?.BodiesOfWater?.find(item => item.UUID === alert.bodyUuid) ?? body;
        } else if (alert?.entityUuid) {
            body = config?.Config?.BodiesOfWater?.find(dev => dev.UUID === alert.entityUuid) ?? body;
        }

        const device = config?.Config?.Devices?.find(dev => dev.UUID === alert?.entityUuid) ?? 
            { Name: 'DeviceUUID: ' + (alert?.entityUuid ?? noValueSign), HardwareDevice: new Config.HardwareDeviceConfig() } as Config.DeviceConfig;

        const alertData = alert?.data || {};

        const firstReported = alertData?.ErrorStartTime ? this.locale.formatDateTime(new Date(alertData?.ErrorStartTime), false) : this.locale.gettext('Unknown');

        const lastSeen = alert?.data?.OfflineDateTime
            ? this.locale.formatDateTime(new Date(alert?.data?.OfflineDateTime), false)
            : this.locale.gettext('Never communicated')
        ;
        
        return {
            body, device, alertData, firstReported, lastSeen
        };
    }
    
    getInfo(alert: AlertRecord = this.alert, config: Config.AllConfig = this.allConfigs): AlertInfo {
        let bodyConfig = undefined;
        let deviceConfig = undefined;
        let entityConfig = undefined;
        let featureConfig = undefined;

        if (alert?.bodyUuid) {
            bodyConfig = config?.Config?.BodiesOfWater?.find(item => item.UUID === alert.bodyUuid) || { UUID: alert?.bodyUuid };
        }    
        if (alert?.entityUuid) {
            bodyConfig = config?.Config?.BodiesOfWater?.find(item => item.UUID === alert.entityUuid);
            deviceConfig = config?.Config?.Devices?.find(item => item.UUID === alert?.entityUuid);
            featureConfig = config?.Config?.WaterFeatures?.find(item => item.UUID === alert?.entityUuid);
            if (!bodyConfig && !deviceConfig && !featureConfig) {
                entityConfig = { UUID: alert?.entityUuid };
            }
        }
        const bodyNameOrUUID = bodyConfig?.Name ?? bodyConfig?.UUID;
        const deviceNameOrUUID = deviceConfig?.Name ?? deviceConfig?.UUID;
        const entityNameOrUUID = entityConfig?.Name ?? entityConfig?.UUID;
        const featureNameOrUUID = featureConfig?.Name ?? featureConfig?.UUID;
        
        // Each type of alert has it's own child class with getInfo() overrided.
        // So in case the getInfo() of the base class below was called, that means it is an unhandled alert.

        let title = (alert?.type ?? "").replaceAll("_", " ").toLocaleLowerCase();
        // Captalize first letter
        title = title.slice(0, 1).toLocaleUpperCase() + title.slice(1);
        const info = AlertInfo.fromCardConfig({
            cardTitle: this.locale.gettext("%s alert", title),
            cardDescription: [
                bodyNameOrUUID && this.locale.gettext("Body: %s", bodyNameOrUUID),
                deviceNameOrUUID && this.locale.gettext("Device: %s", deviceNameOrUUID),
                entityNameOrUUID && this.locale.gettext("Entity: %s", entityNameOrUUID),
                featureNameOrUUID && this.locale.gettext("Water Feature: %s", featureNameOrUUID),
            ]
                .filter(item => item)
                .join("\n")
                .trim()
            ,
            cardIllustration: "status_problem",
            detailDescriptionArr: [
                this.locale.gettext("There is a '%s' alert being reported by:", title),
                bodyNameOrUUID && this.locale.gettext("Body of Water: %s", bodyNameOrUUID),
                deviceNameOrUUID && this.locale.gettext("Device: %s", deviceNameOrUUID),
                entityNameOrUUID && this.locale.gettext("Entity: %s", entityNameOrUUID),
                featureNameOrUUID && this.locale.gettext("Water Feature: %s", featureNameOrUUID),
                this.locale.gettext("No additional information is available.  This can happen if your Attendant reports alerts that the mobile app hasn't been configured for yet."),
                this.locale.gettext("Check the app store to see if updates are available."),
                CommonText.pleaseCallPoolsideSupportContact,
            ].filter(item => item),
            detailIcon: "exclamation-triangle",
        });
        info.contentCategories = [ ContentCategories.SYSTEM_INFO ];
        info.created = alert.created;
        info.updated = alert.updated;
        return info;
    };
}